import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, CardContent, Typography, CircularProgress, Button,
  Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Paper, Container, FormControl, InputLabel, Select, MenuItem, Chip,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BuildIcon from "@mui/icons-material/Build";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useAppSelector } from "../store";
import { getAuthState } from "../store/slices/authSlice";
import Api from "../services/apis/setup";
import { formatDate } from "../utils/helper";
import { useNavigate } from "react-router-dom";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

type ServiceHistory = { _id: string; spareChangedDate: string; partDetails: string; amountOfParts?: number; totalServiceCost?: number; totalPartsCost?: number; description: string; changedBy: string; };
type Vehicle = { _id: string; number: string; odo: number; lastService: string; nextServiceDue: string; nextOilChangeOdo: number; nextGreasingOdo: number; remarks: string; transport: any; history: ServiceHistory[]; createdAt?: string; };
type ActiveView = "vehicles" | "services" | "spent" | "attention" | "default";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();
const allMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const years = Array.from({ length: currentYear - 1990 + 1 }, (_, i) => currentYear - i);

function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [activeView, setActiveView] = useState<ActiveView>("default");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());

  const navigate = useNavigate();
  const { user } = useAppSelector(getAuthState);
  const [fetchAllVehicles] = Api.useLazyFetchAllVehiclesQuery();
  const vehicleList = useAppSelector((state) => state.vehicle.vehicleList);

  useEffect(() => { if (vehicleList && vehicleList.length > 0 && vehicles.length === 0 && !loading) setVehicles(vehicleList as Vehicle[]); }, [vehicleList]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      const callVehicles = async () => {
        try {
          const result: any = await fetchAllVehicles({ baseURL: "/vehicles", params: { page: 1, limit: 1000 } });
          let response = result?.data;
          if (!response) response = result;
          let vehiclesData: Vehicle[] = [];
          if (Array.isArray(response)) vehiclesData = response;
          else if (response?.data) vehiclesData = Array.isArray(response.data) ? response.data : response.data.data;
          setVehicles(vehiclesData);
        } catch (error) { console.error('[Dashboard Fetch] Error:', error); }
        finally { setLoading(false) }
      };
      callVehicles();
    }
  }, [user]);

  const filteredVehicles = useMemo(() => {
    const year = parseInt(selectedYear);
    return vehicles.filter(v => {
      const createdAt = v.createdAt || v.lastService;
      if (!createdAt) return true;
      return new Date(createdAt).getFullYear() === year;
    });
  }, [vehicles, selectedYear]);

  const filteredServices = useMemo(() => {
    const year = parseInt(selectedYear);
    return vehicles.flatMap(v => (v.history || []).filter(s => s.spareChangedDate && new Date(s.spareChangedDate).getFullYear() === year));
  }, [vehicles, selectedYear]);

  const totalVehicles = filteredVehicles.length;
  const totalServices = filteredServices.length;
  const totalCost = filteredServices.reduce((sum: number, s: any) => sum + parseFloat(s.totalServiceCost || s.amountOfParts || s.totalPartsCost || 0), 0);

  const vehiclesNeedingAttention = useMemo(() => filteredVehicles.filter(v => {
    const days = v.nextServiceDue ? Math.floor((new Date(v.nextServiceDue).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
    return days !== null && days <= 7;
  }), [filteredVehicles]);

  // Line chart for vehicles
  const vehiclesData = useMemo(() => {
    const year = parseInt(selectedYear);
    const isCurrentYear = year === currentYear;
    const data: (number | null)[] = allMonths.map((_, i) => {
      if (isCurrentYear && i > currentMonth) return null;
      return 0;
    });
    vehicles.forEach(v => {
      const createdAt = v.createdAt || v.lastService;
      if (createdAt) {
        const d = new Date(createdAt);
        if (d.getFullYear() === year && (!isCurrentYear || d.getMonth() <= currentMonth)) {
          data[d.getMonth()] = ((data[d.getMonth()] as number) || 0) + 1;
        }
      }
    });
    return { labels: allMonths, datasets: [{ label: `Vehicles in ${year}`, data, borderColor: "#062141", backgroundColor: "rgba(6,33,65,0.1)", tension: 0.3, fill: true, pointRadius: 5, pointBackgroundColor: "#062141" }] };
  }, [vehicles, selectedYear]);

  // Line chart for services
  const servicesData = useMemo(() => {
    const year = parseInt(selectedYear);
    const isCurrentYear = year === currentYear;
    const data: (number | null)[] = allMonths.map((_, i) => {
      if (isCurrentYear && i > currentMonth) return null;
      return 0;
    });
    vehicles.forEach(v => (v.history || []).forEach(s => {
      if (s.spareChangedDate) {
        const d = new Date(s.spareChangedDate);
        if (d.getFullYear() === year && (!isCurrentYear || d.getMonth() <= currentMonth)) {
          data[d.getMonth()] = ((data[d.getMonth()] as number) || 0) + 1;
        }
      }
    }));
    return { labels: allMonths, datasets: [{ label: `Services in ${year}`, data, borderColor: "#ff9800", backgroundColor: "rgba(255,152,0,0.1)", tension: 0.3, fill: true, pointRadius: 5, pointBackgroundColor: "#ff9800" }] };
  }, [vehicles, selectedYear]);

  // Bar chart for cost
  const costData = useMemo(() => {
    const year = parseInt(selectedYear);
    const isCurrentYear = year === currentYear;
    const data: (number | null)[] = allMonths.map((_, i) => {
      if (isCurrentYear && i > currentMonth) return null;
      return 0;
    });
    vehicles.forEach(v => (v.history || []).forEach(s => {
      if (s.spareChangedDate) {
        const d = new Date(s.spareChangedDate);
        if (d.getFullYear() === year && (!isCurrentYear || d.getMonth() <= currentMonth)) {
          const costValue = parseFloat(String(s.totalServiceCost || s.amountOfParts || s.totalPartsCost || 0)) || 0;
          const currentValue = (data[d.getMonth()] as number) || 0;
          data[d.getMonth()] = currentValue + costValue;
        }
      }
    }));
    return { labels: allMonths, datasets: [{ label: `Cost (₹) in ${year}`, data, backgroundColor: "rgba(76,175,80,0.8)", borderColor: "#388e3c", borderWidth: 1, borderRadius: 4 }] };
  }, [vehicles, selectedYear]);

  const attentionBreakdown = useMemo(() => ({
    labels: ["Overdue", "Due Soon", "Oil Change", "Greasing"],
    datasets: [{
      data: [
        vehiclesNeedingAttention.filter(v => (v.nextServiceDue ? Math.floor((new Date(v.nextServiceDue).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0) < 0).length,
        vehiclesNeedingAttention.filter(v => { const d = v.nextServiceDue ? Math.floor((new Date(v.nextServiceDue).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0; return d >= 0 && d <= 7; }).length,
        filteredVehicles.filter(v => v.nextOilChangeOdo && v.odo && v.nextOilChangeOdo <= v.odo).length,
        filteredVehicles.filter(v => v.nextGreasingOdo && v.odo && v.nextGreasingOdo <= v.odo).length,
      ],
      backgroundColor: ["#d32f2f", "#f57c00", "#1976d2", "#7b1fa2"], borderWidth: 0,
    }],
  }), [vehiclesNeedingAttention, filteredVehicles]);

  const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top" as const } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } };
  const doughnutOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right" as const } } };

  const handleYearChange = (e: any) => { setSelectedYear(e.target.value); setPage(0); };
  const handleRefresh = () => { setSelectedYear(currentYear.toString()); setPage(0); };

  const renderDetailView = () => {
    const yr = selectedYear;
    switch (activeView) {
      case "vehicles": return <Card sx={{ mt: 3 }}><CardContent><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}><Typography variant="h6" sx={{ fontWeight: 600, color: "#062141" }}>Vehicles in {yr}</Typography><Button variant="contained" startIcon={<VisibilityIcon />} onClick={() => navigate("/")} sx={{ backgroundColor: 'var(--primary-color)' }}>View Details</Button></Box><Box sx={{ height: 350 }}><Line data={vehiclesData} options={chartOptions} /></Box></CardContent></Card>;
      case "services": return <Card sx={{ mt: 3 }}><CardContent><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}><Typography variant="h6" sx={{ fontWeight: 600, color: "#062141" }}>Services in {yr}</Typography><Button variant="contained" startIcon={<VisibilityIcon />} onClick={() => navigate("/service-history")} sx={{ backgroundColor: 'var(--primary-color)' }}>View Details</Button></Box><Box sx={{ height: 350 }}><Line data={servicesData} options={chartOptions} /></Box></CardContent></Card>;
      case "spent": return <Card sx={{ mt: 3 }}><CardContent><Typography variant="h6" sx={{ fontWeight: 600, color: "#062141", mb: 2 }}>Cost in {yr}</Typography><Box sx={{ height: 350 }}><Bar data={costData} options={chartOptions} /></Box></CardContent></Card>;
      case "attention": return <Card sx={{ mt: 3 }}><CardContent><Typography variant="h6" sx={{ fontWeight: 600, color: "#062141", mb: 2 }}>Attention Needed</Typography><Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}><Doughnut data={attentionBreakdown} options={doughnutOptions} /></Box><Typography variant="h6" sx={{ fontWeight: 600, color: "#062141", mt: 4, mb: 2 }}>Vehicles ({vehiclesNeedingAttention.length})</Typography><TableContainer component={Paper} elevation={0}><Table size="small"><TableHead><TableRow sx={{ background: "#f5f7fa" }}><TableCell sx={{ fontWeight: 600 }}>Vehicle</TableCell><TableCell sx={{ fontWeight: 600 }}>Transport</TableCell><TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell><TableCell sx={{ fontWeight: 600 }}>Days</TableCell><TableCell sx={{ fontWeight: 600 }}>Status</TableCell></TableRow></TableHead><TableBody>{vehiclesNeedingAttention.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(v => { const days = v.nextServiceDue ? Math.floor((new Date(v.nextServiceDue).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0; return <TableRow key={v._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/service-history/${v._id}`)}><TableCell sx={{ fontWeight: 500 }}>{v.number}</TableCell><TableCell>{v.transport?.transportName || 'N/A'}</TableCell><TableCell>{formatDate(v.nextServiceDue)}</TableCell><TableCell><Typography variant="body2" sx={{ color: days < 0 ? '#d32f2f' : days <= 3 ? '#f57c00' : '#1976d2', fontWeight: 600 }}>{days < 0 ? `${Math.abs(days)} overdue` : `${days} days`}</Typography></TableCell><TableCell><Typography sx={{ color: days < 0 ? '#d32f2f' : '#f57c00', fontWeight: 500, fontSize: '0.75rem' }}>{days < 0 ? 'OVERDUE' : 'DUE SOON'}</Typography></TableCell></TableRow>; })}</TableBody></Table></TableContainer><TablePagination component="div" count={vehiclesNeedingAttention.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[5, 10, 25]} /></CardContent></Card>;
      default: return <Card sx={{ mt: 3 }}><CardContent><Typography variant="h6" sx={{ fontWeight: 600, color: "#062141", mb: 2 }}>Vehicles in {yr}</Typography><Box sx={{ height: 350 }}><Line data={vehiclesData} options={chartOptions} /></Box></CardContent></Card>;
    }
  };

  if (loading && vehicles.length === 0) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}><Typography variant="h4" sx={{ fontWeight: 700, color: "#062141", mb: 1 }}>Dashboard</Typography><Typography variant="body1" sx={{ color: "#666" }}>Welcome back!</Typography></Box>

        {/* Filter Bar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap', bgcolor: '#f8f9fa', p: 2, borderRadius: 2 }}>
          <CalendarTodayIcon sx={{ color: 'var(--primary-color)', fontSize: 20 }} />
          <FormControl size="small" sx={{ minWidth: 140, bgcolor: 'white', borderRadius: 1 }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear} label="Year" onChange={handleYearChange} sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.15)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' } }}>
              {years.map(y => <MenuItem key={y} value={y.toString()}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" size="small" startIcon={<RefreshIcon />} onClick={handleRefresh} sx={{ backgroundColor: 'var(--primary-color)', textTransform: 'none', px: 2, boxShadow: 'none', '&:hover': { backgroundColor: 'var(--primary-color)', opacity: 0.9 } }}>Refresh</Button>
          {parseInt(selectedYear) === currentYear && <Chip label={`Till ${allMonths[currentMonth]}`} size="small" sx={{ bgcolor: 'rgba(6,33,65,0.1)', color: 'var(--primary-color)' }} />}
        </Box>

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3 }}>
          <Card sx={{ background: "linear-gradient(135deg, #062141 0%, #041a32 100%)", color: "white", cursor: "pointer", transition: "0.3s", "&:hover": { transform: "translateY(-5px)" } }} onClick={() => setActiveView(activeView === "vehicles" ? "default" : "vehicles")}>
            <CardContent><Box display="flex" justifyContent="space-between"><Box><Typography variant="body2" sx={{ opacity: 0.8 }}>Total Vehicles</Typography><Typography variant="h4" sx={{ fontWeight: 700 }}>{totalVehicles}</Typography></Box><DirectionsCarIcon sx={{ fontSize: 40, opacity: 0.6 }} /></Box></CardContent>
          </Card>
          <Card sx={{ background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)", color: "white", cursor: "pointer", transition: "0.3s", "&:hover": { transform: "translateY(-5px)" } }} onClick={() => setActiveView(activeView === "services" ? "default" : "services")}>
            <CardContent><Box display="flex" justifyContent="space-between"><Box><Typography variant="body2" sx={{ opacity: 0.8 }}>Total Services</Typography><Typography variant="h4" sx={{ fontWeight: 700 }}>{totalServices}</Typography></Box><BuildIcon sx={{ fontSize: 40, opacity: 0.6 }} /></Box></CardContent>
          </Card>
          <Card sx={{ background: "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)", color: "white", cursor: "pointer", transition: "0.3s", "&:hover": { transform: "translateY(-5px)" } }} onClick={() => setActiveView(activeView === "spent" ? "default" : "spent")}>
            <CardContent><Box display="flex" justifyContent="space-between"><Box><Typography variant="body2" sx={{ opacity: 0.8 }}>Total Spent</Typography><Typography variant="h4" sx={{ fontWeight: 700 }}>₹{totalCost.toLocaleString()}</Typography></Box><AttachMoneyIcon sx={{ fontSize: 40, opacity: 0.6 }} /></Box></CardContent>
          </Card>
          <Card sx={{ background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)", color: "white", cursor: "pointer", transition: "0.3s", "&:hover": { transform: "translateY(-5px)" } }} onClick={() => setActiveView(activeView === "attention" ? "default" : "attention")}>
            <CardContent><Box display="flex" justifyContent="space-between"><Box><Typography variant="body2" sx={{ opacity: 0.8 }}>Attention</Typography><Typography variant="h4" sx={{ fontWeight: 700 }}>{vehiclesNeedingAttention.length}</Typography></Box><WarningAmberIcon sx={{ fontSize: 40, opacity: 0.6 }} /></Box></CardContent>
          </Card>
        </Box>

        {renderDetailView()}
      </Container>
    </Box>
  );
}

export default Dashboard;